import { toDisplayPersonName } from '../utils/personName';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import UserIcon from 'lucide-react-native/dist/esm/icons/user.js';
import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { z } from 'zod';

import { Header } from '../components/Header';
import { KeyboardAwareFormScrollView } from '../components/shared/KeyboardAwareFormScrollView';
import {
  KeyboardDismissToolbar,
  ACCESSORY_VIEW_ID,
} from '../components/shared/KeyboardDismissToolbar';
import { SubHeader } from '../components/SubHeader';
import { useAuth } from '../contexts/AuthContext';
import { useUserProfile, userProfileKeys } from '../hooks/useUserProfile';
import { networkService } from '../services/network';
import { offlineCache } from '../services/offlineCache';
import { colors } from '../theme';
import { showToast } from '../utils/toast';

const isWeb = Platform.OS === 'web';

/**
 * Validation constants - match server-side limits (AC 10 from issue #712)
 */
const VALIDATION = {
  USER_ROLE_MAX_LENGTH: 255,
  PHONE_MAX_LENGTH: 50,
  PHONE_PATTERN: /^[+\d\s\-().]*$/,
  HTML_PATTERN: /<[^>]*>/,
} as const;

/**
 * Create Zod validation schema for user profile form.
 * Wrapped in a function to avoid module-level initialization issues on iOS.
 */
function createUserProfileSchema() {
  return z.object({
    userRole: z
      .string()
      .max(
        VALIDATION.USER_ROLE_MAX_LENGTH,
        `Role must be ${VALIDATION.USER_ROLE_MAX_LENGTH} characters or less`,
      )
      .refine(
        val => !VALIDATION.HTML_PATTERN.test(val),
        'HTML tags are not allowed',
      ),
    phone: z
      .string()
      .max(
        VALIDATION.PHONE_MAX_LENGTH,
        `Phone must be ${VALIDATION.PHONE_MAX_LENGTH} characters or less`,
      )
      .refine(
        val => val === '' || VALIDATION.PHONE_PATTERN.test(val),
        'Phone can only contain numbers, spaces, and +()-.',
      )
      .refine(
        val => val === '' || val.replace(/\D/g, '').length >= 10,
        'Use a valid phone number (at least 10 digits).',
      ),
  });
}

type UserProfileFormData = {
  userRole: string;
  phone: string;
};

interface UserProfileProps {
  navigate: (path: string) => void;
  currentPath?: string;
}

export const UserProfile: React.FC<UserProfileProps> = ({
  navigate,
  currentPath,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { profile, isLoading, updateProfileAsync, isSaving, error } =
    useUserProfile();

  // Create schema lazily to avoid module-level Zod initialization issues on iOS
  const userProfileSchema = useMemo(() => createUserProfileSchema(), []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty: formDirty },
  } = useForm<UserProfileFormData>({
    resolver: zodResolver(userProfileSchema),
    defaultValues: {
      userRole: '',
      phone: '',
    },
  });

  const isDirty = formDirty;

  // Reset form when profile data loads
  useEffect(() => {
    if (profile) {
      reset({
        userRole: profile.userRole,
        phone: profile.phone,
      });
    }
  }, [profile, reset]);

  const onSubmit = async (data: UserProfileFormData) => {
    if (!isDirty) {
      showToast.info('No changes to save');
      return;
    }

    const isOnline = networkService.isConnected();
    const loadingToast = showToast.loading('Saving profile...');

    if (!isOnline) {
      try {
        await offlineCache.queueProfileUpdate(data);
        await offlineCache.cacheUserProfile(data);
        // Update React Query cache so data displays immediately when navigating back
        try {
          queryClient.setQueryData(userProfileKeys.all, data);
        } catch {
          // Tests / unexpected cache shapes must not block offline save UX
        }
        showToast.dismiss(loadingToast);
        reset(data);
        showToast.success('Profile saved offline. Will sync when connected.');
      } catch {
        showToast.dismiss(loadingToast);
        showToast.error('Failed to save offline');
      }
      return;
    }

    try {
      await updateProfileAsync(data);
      reset(data);
      showToast.dismiss(loadingToast);
      showToast.success('Profile saved successfully!');
    } catch (err) {
      showToast.dismiss(loadingToast);
      showToast.error(
        err instanceof Error ? err.message : 'Failed to save profile',
      );
    }
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <Header currentPath={currentPath || '/profile'} navigate={navigate} />
        <SubHeader title="Profile" onNavigate={navigate} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading profile...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Header currentPath={currentPath || '/profile'} navigate={navigate} />
      <SubHeader title="Profile" onNavigate={navigate} />

      <KeyboardAwareFormScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        testID="profile-scroll-view"
      >
        <View style={styles.contentWrapper}>
          {/* Consolidated Profile section */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <UserIcon size={20} color="#666" />
              <Text style={styles.sectionTitle}>Profile Information</Text>
            </View>
            <View style={styles.sectionContent}>
              {/* Row 1: Name and Email */}
              <View style={styles.fieldRow}>
                <View style={styles.fieldColumn}>
                  <Text style={styles.fieldLabel}>Name</Text>
                  <View style={styles.readOnlyField}>
                    <Text testID="profile-name" style={styles.readOnlyText}>
                      {toDisplayPersonName(user?.name)}
                    </Text>
                  </View>
                </View>
                <View style={styles.fieldColumn}>
                  <Text style={styles.fieldLabel}>Email</Text>
                  <View style={styles.readOnlyField}>
                    <Text testID="profile-email" style={styles.readOnlyText}>
                      {user?.email || ''}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Row 2: Role and Phone */}
              <View style={styles.fieldRow}>
                <View style={styles.fieldColumn}>
                  <Text style={styles.fieldLabel}>Role</Text>
                  <Controller
                    control={control}
                    name="userRole"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        style={[
                          styles.textInput,
                          errors.userRole && styles.textInputError,
                        ]}
                        onBlur={onBlur}
                        onChangeText={onChange}
                        value={value}
                        placeholder="e.g., Field Engineer, Site Manager"
                        placeholderTextColor={colors.inputPlaceholder}
                        maxLength={VALIDATION.USER_ROLE_MAX_LENGTH}
                        testID="profile-role-input"
                        accessibilityLabel="Role"
                        accessibilityHint="Enter your role, e.g., Field Engineer"
                      />
                    )}
                  />
                  {errors.userRole && (
                    <Text style={styles.fieldErrorText}>
                      {errors.userRole.message}
                    </Text>
                  )}
                </View>
                <View style={styles.fieldColumn}>
                  <Text style={styles.fieldLabel}>Phone</Text>
                  <Controller
                    control={control}
                    name="phone"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        style={[
                          styles.textInput,
                          errors.phone && styles.textInputError,
                        ]}
                        onBlur={onBlur}
                        onChangeText={onChange}
                        value={value}
                        placeholder="e.g., +1 (555) 123-4567"
                        placeholderTextColor={colors.inputPlaceholder}
                        keyboardType="phone-pad"
                        maxLength={VALIDATION.PHONE_MAX_LENGTH}
                        testID="profile-phone-input"
                        accessibilityLabel="Phone"
                        accessibilityHint="Enter your phone number"
                        {...(Platform.OS === 'ios' && {
                          inputAccessoryViewID: ACCESSORY_VIEW_ID,
                        })}
                      />
                    )}
                  />
                  {errors.phone && (
                    <Text style={styles.fieldErrorText}>
                      {errors.phone.message}
                    </Text>
                  )}
                </View>
              </View>

              {/* Web: Save button inside card */}
              {isWeb && (
                <View style={styles.cardActionSection}>
                  {/* testID: tests need ONE obvious save control; multiple Touchables caused "which one?" flakiness */}
                  <TouchableOpacity
                    style={[
                      styles.saveButton,
                      (isSaving || !isDirty) && styles.saveButtonDisabled,
                    ]}
                    onPress={handleSubmit(onSubmit)}
                    disabled={isSaving || !isDirty}
                    testID="profile-save-button"
                    accessibilityLabel="Save profile"
                    accessibilityHint={
                      isDirty
                        ? 'Save your profile changes'
                        : 'No changes to save'
                    }
                  >
                    {isSaving ? (
                      <ActivityIndicator
                        size="small"
                        color={colors.textLight}
                      />
                    ) : (
                      <Text style={styles.saveButtonText}>Save</Text>
                    )}
                  </TouchableOpacity>

                  {error && (
                    <Text style={styles.errorMessage}>
                      {error instanceof Error
                        ? error.message
                        : 'An error occurred'}
                    </Text>
                  )}
                </View>
              )}
            </View>
          </View>
        </View>
      </KeyboardAwareFormScrollView>
      <KeyboardDismissToolbar />

      {/* Mobile: Sticky footer — same testID as web so tests target one stable "Save" handle */}
      {!isWeb && (
        <View style={styles.footer}>
          <TouchableOpacity
            style={[
              styles.saveButton,
              (isSaving || !isDirty) && styles.saveButtonDisabled,
            ]}
            onPress={handleSubmit(onSubmit)}
            disabled={isSaving || !isDirty}
            testID="profile-save-button"
            accessibilityLabel="Save profile"
            accessibilityHint={
              isDirty ? 'Save your profile changes' : 'No changes to save'
            }
          >
            {isSaving ? (
              <ActivityIndicator size="small" color={colors.textLight} />
            ) : (
              <Text style={styles.saveButtonText}>Save</Text>
            )}
          </TouchableOpacity>

          {error && (
            <Text style={styles.errorMessage}>
              {error instanceof Error ? error.message : 'An error occurred'}
            </Text>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 60,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    alignItems: isWeb ? 'center' : ('stretch' as const),
    flexGrow: 1,
    ...Platform.select({
      ios: { paddingBottom: 80 },
      android: { paddingBottom: 80 },
    }),
  },
  contentWrapper: {
    width: '100%',
    maxWidth: isWeb ? 1200 : undefined,
    alignSelf: isWeb ? 'center' : ('stretch' as const),
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    marginBottom: 20,
    overflow: 'hidden',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
    gap: 12,
    backgroundColor: '#f8f9fa',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  sectionContent: {
    padding: 16,
  },
  fieldRow: {
    flexDirection: isWeb ? 'row' : 'column',
    gap: isWeb ? 20 : 0,
    marginBottom: 16,
  },
  fieldColumn: {
    flex: 1,
    marginBottom: isWeb ? 0 : 16,
  },
  fieldGroup: {
    marginBottom: 16,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.textSecondary,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  readOnlyField: {
    backgroundColor: colors.backgroundMuted,
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.borderDark,
  },
  readOnlyText: {
    fontSize: 15,
    color: colors.textPrimary,
  },
  textInput: {
    backgroundColor: colors.backgroundPrimary,
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    fontSize: 15,
    color: colors.textPrimary,
  },
  textInputError: {
    borderColor: colors.inputBorderError,
    borderWidth: 1,
  },
  fieldErrorText: {
    fontSize: 12,
    color: colors.error,
    marginTop: 4,
  },
  cardActionSection: {
    marginTop: 16,
    paddingTop: 20,
    paddingBottom: 4,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    alignItems: 'flex-end',
  },
  footer: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    ...Platform.select({
      ios: {
        paddingBottom: 34,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 8,
      },
    }),
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 80,
    minHeight: 40,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    color: colors.textLight,
    fontSize: 16,
    fontWeight: '600',
  },
  errorMessage: {
    marginTop: 12,
    fontSize: 14,
    color: colors.error,
    fontWeight: '500',
  },
});
